import { useState, useRef } from 'react';
import { UploadCloud, CheckCircle, XCircle, Loader } from 'lucide-react';

const UPLOAD_API_URL = 'https://manager.event2one.com/videos/upload-to-youtube/upload_api.php';
const UPLOAD_API_KEY = 'mgv_yt_upload_2026';

/**
 * Props :
 *   idConfEvent      {string|number}
 *   idContact        {string|number}
 *   title            {string}
 *   onUploadComplete {(idVideo: string) => void}
 */
const DragDropVideoUpload = ({ idConfEvent, idContact, idVideo, title = 'Vidéo webcast', onUploadComplete, idEvent, organisationId = 1, compact = false, lang = 'fr' }) => {
    const [status, setStatus] = useState('idle'); // idle | dragging | uploading | done | error
    const [progress, setProgress] = useState(0);
    const [errorMsg, setErrorMsg] = useState('');
    const [fileName, setFileName] = useState('');
    const inputRef = useRef(null);

    const handleFile = (file) => {
        if (!file || !file.type.startsWith('video/')) {
            setErrorMsg('Veuillez sélectionner un fichier vidéo.');
            setStatus('error');
            return;
        }

        setFileName(file.name);
        setStatus('uploading');
        setProgress(0);
        setErrorMsg('');

        const formData = new FormData();
        formData.append('video', file);
        formData.append('title', title);
        formData.append('id_conf_event', idConfEvent ?? '');
        formData.append('id_contact', idContact ?? '');
        formData.append('id_event', idEvent ?? '');
        formData.append('organisation_id', organisationId);
        formData.append('privacy_status', 'public');
        formData.append('lang', lang);
        if (idVideo) formData.append('id_video', idVideo);

        const xhr = new XMLHttpRequest();

        xhr.upload.onprogress = (e) => {
            if (e.lengthComputable) setProgress(Math.round((e.loaded / e.total) * 100));
        };

        xhr.onload = () => {
            if (xhr.status >= 200 && xhr.status < 300) {
                try {
                    const data = JSON.parse(xhr.responseText);
                    if (data.error) throw new Error(data.error);
                    setStatus('done');
                    onUploadComplete?.(data);
                } catch (e) {
                    setErrorMsg(e.message || 'Réponse invalide du serveur');
                    setStatus('error');
                }
            } else {
                setErrorMsg(`Erreur serveur (${xhr.status})`);
                setStatus('error');
            }
        };

        xhr.onerror = () => {
            setErrorMsg('Erreur réseau pendant l\'upload');
            setStatus('error');
        };

        xhr.open('POST', UPLOAD_API_URL);
        xhr.setRequestHeader('X-Api-Key', UPLOAD_API_KEY);
        xhr.send(formData);
    };

    const onDrop = (e) => {
        e.preventDefault();
        setStatus('idle');
        const file = e.dataTransfer.files?.[0];
        if (file) handleFile(file);
    };

    const onDragOver = (e) => { e.preventDefault(); setStatus('dragging'); };
    const onDragLeave = () => { if (status === 'dragging') setStatus('idle'); };
    const onInputChange = (e) => { const file = e.target.files?.[0]; if (file) handleFile(file); };
    const reset = () => { setStatus('idle'); setProgress(0); setFileName(''); setErrorMsg(''); };

    const borderColor = {
        idle: 'border-gray-300',
        dragging: 'border-blue-400 bg-blue-50',
        uploading: 'border-blue-300',
        done: 'border-green-400 bg-green-50',
        error: 'border-red-400 bg-red-50',
    }[status];

    return (
        <div
            className={`relative border-2 border-dashed rounded-lg text-center transition-colors cursor-pointer flex flex-col items-center justify-center ${compact ? 'p-3 min-h-[76px]' : 'p-6 min-h-[120px]'} ${borderColor}`}
            onDrop={onDrop}
            onDragOver={onDragOver}
            onDragLeave={onDragLeave}
            onClick={() => status === 'idle' && inputRef.current?.click()}
        >
            <input
                ref={inputRef}
                type="file"
                accept="video/*"
                className="hidden"
                onChange={onInputChange}
            />

            {status === 'idle' && (
                <div className="flex flex-col items-center gap-2 text-gray-500">
                    <UploadCloud size={compact ? 22 : 36} className="text-gray-400" />
                    <span className="text-sm font-medium">Glisser une vidéo ici</span>
                    {!compact && <span className="text-xs text-gray-400">ou cliquer pour parcourir</span>}
                </div>
            )}

            {status === 'dragging' && (
                <div className="flex flex-col items-center gap-2 text-blue-600">
                    <UploadCloud size={36} />
                    <span className="text-sm font-medium">Déposer pour uploader</span>
                </div>
            )}

            {status === 'uploading' && (
                <div className="flex flex-col items-center gap-2">
                    <Loader size={22} className="text-blue-500 animate-spin" />
                    <span className="text-xs text-gray-600 truncate max-w-full">{fileName}</span>
                    <div className="w-full bg-gray-200 rounded-full h-2 mt-1">
                        <div
                            className="bg-blue-500 h-2 rounded-full transition-all duration-200"
                            style={{ width: `${progress}%` }}
                        />
                    </div>
                    <span className="text-xs text-gray-500">{progress}%</span>
                </div>
            )}

            {status === 'done' && (
                <div className="flex flex-col items-center gap-1 text-green-700">
                    <CheckCircle size={24} />
                    <span className="text-sm font-medium">Vidéo uploadée</span>
                    <span className="text-xs text-gray-500 truncate max-w-full">{fileName}</span>
                    <button
                        type="button"
                        className="mt-1 text-xs text-gray-400 hover:text-gray-600 underline"
                        onClick={(e) => { e.stopPropagation(); reset(); }}
                    >
                        Uploader une autre vidéo
                    </button>
                </div>
            )}

            {status === 'error' && (
                <div className="flex flex-col items-center gap-1 text-red-700">
                    <XCircle size={24} />
                    <span className="text-sm font-medium">Échec de l'upload</span>
                    <span className="text-xs text-red-500">{errorMsg}</span>
                    <button
                        type="button"
                        className="mt-1 text-xs text-gray-400 hover:text-gray-600 underline"
                        onClick={(e) => { e.stopPropagation(); reset(); }}
                    >
                        Réessayer
                    </button>
                </div>
            )}
        </div>
    );
};

export default DragDropVideoUpload;
